import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {isOk} from '../../../utils/apiResponse';
import {ScreenTracker} from '../../../utils/ScreenTracker';
import {
    EMPTY_REASON, emptyAccountsReason, emptyActionPath, explainsEmptyAccounts, isSeparateGoogleAccess,
} from './linkAccountEmpty';
import './QuickConnectAccountModal.scss';

const NS = 'control_central.link_account_empty';
const RAW = {escapeValue: false};

/**
 * Pide las conexiones de la agencia y dice por qué la plataforma no tiene cuentas para vincular
 * (`linkAccountEmpty`). Nunca rechaza: si no llegan, el porqué es `UNKNOWN`.
 */
export function fetchEmptyAccountsReason(channel) {
    if (!explainsEmptyAccounts(channel)) return Promise.resolve(null);
    return new APIClient(`${API_URL}agency/connections/`).call('GET')
        .then((response) => emptyAccountsReason(isOk(response) ? response.data : null, channel))
        .catch(() => EMPTY_REASON.UNKNOWN);
}

/** El evento de uso del vacío (`UserScreenActivity`): `link_empty_<shown|click>_<porqué>_<canal>`. */
export function emptyEventName(action, kind, channel) {
    return `link_empty_${action}_${kind}_${String(channel).toLowerCase()}`;
}

/**
 * El vacío de un modal Vincular cuenta (UD-9): por qué no hay ninguna cuenta para elegir y qué hacer,
 * con el botón que lleva a hacerlo y «Ya lo hice, actualizar» para quien volvió de Conexiones.
 * Reemplaza al «No hay resultados.» del combo, que no decía nada.
 *
 * Cuenta cuántas veces se muestra y cuántas se toca el botón, por porqué y por canal: es la medición
 * de la decisión (`SSOT/ux/decisiones.md`).
 */
export function LinkAccountEmptyState({channel, channelName, reason, onRefresh}) {
    const {t} = useTranslation();
    const kind = reason || EMPTY_REASON.UNKNOWN;
    const tOpts = {channel: channelName, interpolation: RAW};
    const actionPath = emptyActionPath(kind, channel);

    useEffect(() => {
        ScreenTracker.recordEvent(emptyEventName('shown', kind, channel));
    }, [kind, channel]);

    return (
        <div className="link-account-state" data-testid="link-account-empty" data-reason={kind}>
            <h4>{t(`${NS}.${kind}.title`, tOpts)}</h4>
            <p>{t(`${NS}.${kind}.text`, tOpts)}</p>
            {kind === EMPTY_REASON.NOT_CONNECTED && isSeparateGoogleAccess(channel) && (
                <p>{t(`${NS}.not_connected.google_note`, tOpts)}</p>
            )}
            <div className="link-account-state__actions">
                {actionPath && (
                    <a className="btn -alt" href={actionPath} target="_blank" rel="noopener noreferrer"
                       onClick={() => ScreenTracker.recordEvent(emptyEventName('click', kind, channel))}>
                        {t(`${NS}.${kind}.action`, tOpts)}
                    </a>
                )}
                <button type="button" className="btn -negative" onClick={onRefresh}>
                    {t(kind === EMPTY_REASON.SYNCING ? `${NS}.syncing.refresh` : 'control_central.link_account_refresh')}
                </button>
            </div>
        </div>
    );
}
